import { useMemo } from 'react'
import { useInventoryLocationsQuery, useServingMiniDistributionCentersQuery } from '../../../graphql'
import { MAIN_DISTRIBUTION_CENTER_ID } from '../RequestDetails.constants'

/** A DC a stock request or fulfillment order can be sent to. */
export type DistributionCenterOption = {
	id: string,
	name: string,
	isWarehouse: boolean,
}

export type UseDistributionCenterOptionsReturn = {
	/** Either lookup is in flight; the options are not final yet */
	loading: boolean,
	/** Whether a mini-DC serves the requesting location's branch zip */
	servedByMiniDc: boolean,
	/**
	 * The DCs the requesting location may be fulfilled from: its serving mini-DCs
	 * then the main DC, or only the main DC when no mini-DC serves it. Empty until
	 * the main DC has loaded.
	 */
	options: DistributionCenterOption[],
	/** The main DC, once loaded */
	mainDistributionCenter?: DistributionCenterOption,
}

/**
 * Which DCs a requesting inventory location may be fulfilled from. The rule both
 * DC pickers follow: a location no mini-DC serves always goes to the main DC, so
 * the picker is locked to it; a served location may pick a serving mini-DC or the
 * main DC. No requesting location yet counts as not served.
 */
export default function useDistributionCenterOptions( requestingInventoryId?: string | null ): UseDistributionCenterOptionsReturn {
	const { data: mainDcData, loading: mainDcLoading } = useInventoryLocationsQuery( {
		variables: {
			inventoryLocationIds: [ MAIN_DISTRIBUTION_CENTER_ID ],
		},
	} )

	const { data: servingData, loading: servingLoading } = useServingMiniDistributionCentersQuery( {
		variables: { inventoryLocationId: requestingInventoryId ?? '' },
		skip: !requestingInventoryId,
	} )

	const mainDistributionCenter = useMemo( () => {
		const node = mainDcData?.inventoryLocations?.edges?.[ 0 ]?.node
		return node ? { id: node.id, name: node.name, isWarehouse: node.isWarehouse } : undefined
	}, [ mainDcData ] )

	// Only trust an answer for the location currently asked about: a skipped or
	// re-keyed query can still hold the previous location's result.
	const miniDcs = useMemo( () => {
		const node = servingData?.inventoryLocations?.edges?.[ 0 ]?.node
		if ( !requestingInventoryId || node?.id !== requestingInventoryId ) {
			return []
		}
		return node.servingMiniDistributionCenters
			.filter( ( dc ) => dc.id !== MAIN_DISTRIBUTION_CENTER_ID )
			.map( ( dc ) => ( { id: dc.id, name: dc.name, isWarehouse: dc.isWarehouse } ) )
	}, [ servingData, requestingInventoryId ] )

	const options = useMemo(
		() => [ ...miniDcs, ...( mainDistributionCenter ? [ mainDistributionCenter ] : [] ) ],
		[ miniDcs, mainDistributionCenter ],
	)

	return {
		loading: mainDcLoading || servingLoading,
		servedByMiniDc: miniDcs.length > 0,
		options,
		mainDistributionCenter,
	}
}
